import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { UploadingPhoto, PhotoUploadResponse, PhotoContextAdapter } from '../types';
import pLimit from 'p-limit';

interface UsePhotoUploadsProps {
  reportId: string;
  photoContext: Pick<PhotoContextAdapter, 'uploadingPhotos' | 'setUploadingPhotos'>;
  uploadPhoto: (reportId: string, file: File, onProgress?: (progress: number) => void, sortOrder?: number) => Promise<PhotoUploadResponse>;
  onPhotoUploaded?: (photo: PhotoUploadResponse, options?: { insertIndex?: number }) => void;
  onBatchComplete?: () => void;
  getBaseIndex?: () => number;
}

const MAX_PHOTO_FILE_SIZE = 30 * 1024 * 1024; // 30MB
const DEFAULT_CONCURRENT_UPLOADS = 4;
const CONCURRENT_UPLOADS =
  typeof navigator !== 'undefined'
    ? Math.min(8, Math.max(DEFAULT_CONCURRENT_UPLOADS, (navigator.hardwareConcurrency || DEFAULT_CONCURRENT_UPLOADS) - 1))
    : DEFAULT_CONCURRENT_UPLOADS;
const limit = pLimit(CONCURRENT_UPLOADS);
// Lower quality speeds conversion and reduces upload sizes; adjust if fidelity is critical.
const HEIC_QUALITY = 0.7;

const isHeicFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  return file.type === 'image/heic' || file.type === 'image/heif' || lowerName.endsWith('.heic') || lowerName.endsWith('.heif');
};

const revokePreviewUrl = (previewUrl?: string) => {
  if (previewUrl && previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl);
  }
};

export const usePhotoUploads = ({
  reportId,
  photoContext,
  uploadPhoto,
  onPhotoUploaded,
  onBatchComplete,
  getBaseIndex,
}: UsePhotoUploadsProps) => {
  const { uploadingPhotos, setUploadingPhotos } = photoContext;
  const heicToRef = useRef<((options: { blob: Blob; type: string; quality?: number }) => Promise<Blob>) | null>(null);

  const createImagePreview = useCallback((file: File): Promise<string> => {
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      return Promise.resolve(URL.createObjectURL(file));
    }

    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        resolve(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const getHeicTo = useCallback(async () => {
    if (heicToRef.current) {
      return heicToRef.current;
    }

    let heicTo: ((options: { blob: Blob; type: string; quality?: number }) => Promise<Blob>) | undefined;
    if (typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined') {
      try {
        ({ heicTo } = await import('heic-to/next'));
      } catch (error) {
        console.warn('Falling back to main-thread HEIC conversion:', error);
      }
    }

    if (!heicTo) {
      ({ heicTo } = await import('heic-to'));
    }

    heicToRef.current = heicTo;
    return heicTo;
  }, []);

  const convertHeicToJpeg = useCallback(
    async (file: File): Promise<File> => {
      try {
        const heicTo = await getHeicTo();
        const convertedBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: HEIC_QUALITY,
        });

        return new File([convertedBlob], file.name.replace(/\.(heic|HEIC)$/, '.jpg'), {
          type: 'image/jpeg',
        });
      } catch (error) {
        console.error('Error converting HEIC to JPEG:', error);
        throw new Error('Failed to convert HEIC image to JPEG');
      }
    },
    [getHeicTo],
  );

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      // Validate file sizes and filter out oversized files
      const oversizedFiles = files.filter(file => file.size > MAX_PHOTO_FILE_SIZE);
      const validFiles = files.filter(file => file.size <= MAX_PHOTO_FILE_SIZE);

      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map(file => file.name).join(', ');
        toast.error(`The following files exceed the 30MB limit and were not uploaded: ${fileNames}`);
      }

      // If no valid files remain, return early
      if (validFiles.length === 0) {
        return;
      }

      // Sort files by filename (numeric-aware) so sort orders are assigned in the correct order
      const sortedFiles = [...validFiles].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

      // baseIndex is max(existing sortOrder) + 1, so new sort orders continue strictly
      // above the highest existing value — handles deletions/gaps without collisions.
      const baseIndex = getBaseIndex ? getBaseIndex() : 0;

      const newUploadingPhotos: UploadingPhoto[] = await Promise.all(
        sortedFiles.map(async (file, index) => {
          const photoId = `uploading-${Date.now()}-${Math.random()}`;
          const heicFile = isHeicFile(file);
          const preview = heicFile ? '' : await createImagePreview(file);

          const assignedSortOrder = baseIndex + index;

          return {
            id: photoId,
            file,
            preview,
            previewUrl: preview,
            progress: 0,
            status: heicFile ? 'converting' : 'uploading',
            // orderIndex drives UI insert position; sortOrder drives the DB value
            orderIndex: assignedSortOrder,
            sortOrder: assignedSortOrder,
          };
        }),
      );

      setUploadingPhotos(prev => [...prev, ...newUploadingPhotos]);

      // Process all files in parallel using Promise.all
      const uploadPromises = newUploadingPhotos.map(uploadingPhoto =>
        limit(async () => {
          try {
            // Convert HEIC if needed
            let processedFile = uploadingPhoto.file;
            if (isHeicFile(uploadingPhoto.file)) {
              processedFile = await convertHeicToJpeg(uploadingPhoto.file);
              const previewUrl = await createImagePreview(processedFile);
              revokePreviewUrl(uploadingPhoto.previewUrl);
              setUploadingPhotos(prev =>
                prev.map(photo =>
                  photo.id === uploadingPhoto.id ? { ...photo, preview: previewUrl, previewUrl, status: 'uploading' as const } : photo,
                ),
              );
            } else {
              // Ensure status is uploading for non-HEIC files
              setUploadingPhotos(prev =>
                prev.map(photo => (photo.id === uploadingPhoto.id ? { ...photo, status: 'uploading' as const } : photo)),
              );
            }

            // Upload the file, passing the pre-assigned sortOrder so the backend
            // stores the correct distinct value even under concurrent uploads.
            const uploadedPhoto = await uploadPhoto(
              reportId,
              processedFile,
              progress => {
                setUploadingPhotos(prev => prev.map(photo => (photo.id === uploadingPhoto.id ? { ...photo, progress } : photo)));
              },
              uploadingPhoto.sortOrder,
            );

            // Update status to success
            setUploadingPhotos(prev =>
              prev.map(photo => (photo.id === uploadingPhoto.id ? { ...photo, status: 'success' as const } : photo)),
            );

            // Call callback and WAIT for it to complete before removing from uploading list
            if (onPhotoUploaded) {
              await onPhotoUploaded(uploadedPhoto, { insertIndex: uploadingPhoto.orderIndex });
            }

            // Remove from uploading photos AFTER the photo is added to the main photos list
            setUploadingPhotos(prev => {
              const photoToRemove = prev.find(photo => photo.id === uploadingPhoto.id);
              revokePreviewUrl(photoToRemove?.previewUrl);
              return prev.filter(photo => photo.id !== uploadingPhoto.id);
            });

            return uploadedPhoto;
          } catch (error) {
            console.error('Error uploading photo:', error);

            // Update status to error
            setUploadingPhotos(prev =>
              prev.map(photo =>
                photo.id === uploadingPhoto.id
                  ? {
                      ...photo,
                      status: 'error' as const,
                      error: error instanceof Error ? error.message : 'Upload failed',
                    }
                  : photo,
              ),
            );

            toast.error(`Failed to upload ${uploadingPhoto.file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            throw error;
          }
        }),
      );

      // Wait for all uploads to complete (or fail)
      try {
        toast.info(
          `Uploading ${validFiles.length} photo(s)... You can work on other modules while the photos are uploading in the background.`,
        );
        await Promise.all(uploadPromises);
        toast.success(`Uploaded ${validFiles.length} photo(s) successfully`);
      } catch (error) {
        // Individual errors are already handled in the map function above
        // This catch block ensures the function doesn't throw unhandled errors
        toast.error(`Some of the photos were not uploaded successfully`);
      } finally {
        // Always run a single final sort pass once all queued uploads in this batch have settled
        onBatchComplete?.();
      }
    },
    [reportId, createImagePreview, convertHeicToJpeg, getBaseIndex, onBatchComplete, onPhotoUploaded, setUploadingPhotos, uploadPhoto],
  );

  const movePhoto = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setUploadingPhotos(prev => {
        const newPhotos = [...prev];
        const draggedPhoto = newPhotos[dragIndex];
        newPhotos.splice(dragIndex, 1);
        newPhotos.splice(hoverIndex, 0, draggedPhoto);
        return newPhotos;
      });
    },
    [setUploadingPhotos],
  );

  const removeUploadingPhoto = useCallback(
    (photoId: string) => {
      setUploadingPhotos(prev => {
        const photoToRemove = prev.find(photo => photo.id === photoId);
        revokePreviewUrl(photoToRemove?.previewUrl);
        return prev.filter(photo => photo.id !== photoId);
      });
    },
    [setUploadingPhotos],
  );

  return {
    handleFilesSelected,
    movePhoto,
    removeUploadingPhoto,
  };
};
